import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { useRealtime } from "@/context/RealtimeContext";
import { ensureCipherIdentity } from "@/lib/crypto/cipherlock";
import { healChat, healCircle, runHealQueue, notifyUnlocked } from "@/lib/crypto/cipherHeal";

// App-wide Cipher Lock heal (cc_25): publishes this device's bundle on sign-in (a NEW fp makes the
// server fan out `cipherlock.heal` to peers), pulls GET /pqc/heal-queue on socket connect + tab focus,
// and answers `cipherlock.heal` / `cipherlock.session` / `cipherlock.group` frames with a HEAL-ONLY
// ensure. Never mints a session from the background.
export function CipherHealWatcher() {
  const { user } = useAuth();
  const { subscribe, connEpoch } = useRealtime();
  const qc = useQueryClient();
  const userId = user?.id;
  const lastQueueAt = useRef(0);

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    const queue = async (force) => {
      const now = Date.now();
      if (!force && now - lastQueueAt.current < 30000) return;
      lastQueueAt.current = now;
      const n = await runHealQueue(userId);
      if (!cancelled && n) {
        notifyUnlocked(n);
        qc.invalidateQueries({ queryKey: ["messages"] });
      }
    };
    (async () => {
      try { await ensureCipherIdentity(userId); } catch { /* offline — heal-queue below still runs */ }
      if (!cancelled) queue(true);
    })();
    const onVisible = () => { if (document.visibilityState === "visible") queue(false); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, connEpoch, qc]);

  useEffect(() => {
    if (!userId) return undefined;
    return subscribe(async (evt) => {
      const t = evt?.type;
      if (t !== "cipherlock.heal" && t !== "cipherlock.session" && t !== "cipherlock.group") return;
      const chatId = evt.chat_id || evt.session?.chat_id;
      const circleId = evt.circle_id || evt.session?.circle_id;
      let n = 0;
      if (chatId) n += await healChat(userId, chatId);
      if (circleId) n += await healCircle(userId, circleId);
      if (n) {
        notifyUnlocked(n);
        if (chatId) qc.invalidateQueries({ queryKey: ["messages", chatId] });
        if (circleId) qc.invalidateQueries({ queryKey: ["messages"] });
      }
    });
  }, [subscribe, userId, qc]);

  return null;
}
